// filepath: apps/web/src/lib/highlight.ts
//
// Build-time syntax highlighter for the docs site. Uses Shiki's
// fine-grained bundle: each language + theme is dynamic-imported,
// so Vite emits one chunk per grammar instead of bundling all 600+.
// The highlighter is a singleton — `getHighlighter()` resolves once
// and every `highlight()` call after that is synchronous.
//
// IMPORTANT: When the docs site adds a new language, add it to
// LANG_LOADERS (dynamic import fn). When it adds a theme, add it
// to THEMES (dynamic import fn).

import { createHighlighterCore, type HighlighterCore } from 'shiki/core';
import { createJavaScriptRegexEngine } from 'shiki/engine/javascript';

// ── Supported languages (public API) ──────────────────────────
//
// Keep this list small — each grammar is a separate chunk the
// browser fetches on first use.
export const SUPPORTED_LANGUAGES = [
  'tsx',
  'ts',
  'jsx',
  'js',
  'bash',
  'json',
  'html',
  'css',
  'sql',
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// ── Dynamic loaders ──────────────────────────────────────────
//
// Shiki grammars are dynamic ESM imports. Vite splits each one
// into its own chunk; the browser fetches them on first docs
// page load. We never bundle them statically because that
// would defeat the purpose of fine-grained imports.
const LANG_LOADERS = {
  tsx: () => import('@shikijs/langs/tsx'),
  ts: () => import('@shikijs/langs/ts'),
  jsx: () => import('@shikijs/langs/jsx'),
  js: () => import('@shikijs/langs/js'),
  bash: () => import('@shikijs/langs/bash'),
  json: () => import('@shikijs/langs/json'),
  html: () => import('@shikijs/langs/html'),
  css: () => import('@shikijs/langs/css'),
  sql: () => import('@shikijs/langs/sql'),
} as const satisfies Record<SupportedLanguage, () => Promise<{ default: unknown }>>;

// Single theme — dark surface matches `.osd-code-block`. Add
// more themes here when a light mode is added.
const THEMES = {
  'github-dark': () => import('@shikijs/themes/github-dark'),
} as const;

const THEME = 'github-dark' as const;

// ── Highlighter singleton ────────────────────────────────────
//
// `createHighlighterCore` is async; once resolved, `codeToHtml`
// is sync. We cache the promise so multiple <CodeBlock> instances
// share the same highlighter (and the same dynamic imports).
let highlighterPromise: Promise<HighlighterCore> | null = null;

async function loadLangs(): Promise<unknown[]> {
  // `Promise.all` so grammars load in parallel.
  return Promise.all(SUPPORTED_LANGUAGES.map((name) => LANG_LOADERS[name]()));
}

function getHighlighter(): Promise<HighlighterCore> {
  highlighterPromise ??= (async () => {
    const [langs, themeModules] = await Promise.all([loadLangs(), THEMES[THEME]()]);
    return createHighlighterCore({
      themes: [themeModules.default],
      // @ts-expect-error — Shiki's lang types are permissive unions;
      // our dynamic-import shape matches at runtime.
      langs,
      // Use the JS regex engine — avoids shipping onig.wasm
      // (1.5MB) to the client.
      engine: createJavaScriptRegexEngine(),
    });
  })();
  return highlighterPromise;
}

// ── Public API ───────────────────────────────────────────────

export interface HighlightResult {
  readonly html: string;
  readonly language: SupportedLanguage;
}

/**
 * Render `code` as syntax-highlighted HTML using the preloaded
 * theme. Returns the highlighted HTML as a string. Safe to call
 * from a React render path — the highlighter Promise is cached
 * and only initialised once.
 */
export async function highlight(
  code: string,
  language: SupportedLanguage,
): Promise<HighlightResult> {
  const hl = await getHighlighter();
  const html = hl.codeToHtml(code, {
    lang: language,
    theme: THEME,
  });
  return { html, language };
}

/**
 * Convenience for component bodies: returns just the HTML string.
 */
export async function highlightToHtml(code: string, language: SupportedLanguage): Promise<string> {
  const result = await highlight(code, language);
  return result.html;
}
